import { createContext, PropsWithChildren, useReducer, useState } from "react";
import * as Sentry from "@sentry/react";
import { ThemeProvider } from "styled-components";
import { GlobalStyleDefaults } from "manifest-components/src/Styles/globals";
import UserDetails from "manifest-components/src/classes/userDetails";
import UserEmployerDetails from "manifest-components/src/classes/userEmployerDetails";
import { useNavigate } from "react-router-dom";
import { getVendor, SlugValidation, VendorConfigIds } from "manifest-components/src/classes/vendorConfig";
import Authentication from "../../utils/authentication";
import AppReducer from "./appReducer";
import Session from "../../utils/session";
import { AppState } from "./appState";
import API from "../../utils/api";
import { route, RoutePaths } from "../../routes/RoutePaths";
import { Actions } from "./appActions";
import { isDevelopmentEnvironment, isE2EEnvironment } from "../../env";
import Analytics, { HeapEventAction } from "../../utils/analytics";

export type AppProviderValue = ReturnType<typeof CreateProviderValue>;

const manifestIRAPartners = ["Futuremoney", "Betterment"];

export const localAPI = {
  getUser: API.user.getUser,
  getUserDetails: API.user.getUserDetails,
  updateUserDetails: API.user.updateUserDetails,
  forgotPassword: API.auth.forgotPassword,
  updateEmail: API.user.updateEmail,
  updatePhoneNumber: API.user.updatePhoneNumber,
  getCountriesList: API.user.getCountriesList,
  getStatesList: API.user.getStatesList,
  getSuffixList: API.user.getSuffixList,
  getEmployerDetails: API.user.getEmployerDetails,
  login: API.auth.login,
  resetPassword: API.auth.resetPassword,
  verifyEmail: API.auth.verifyEmail,
  changePassword: API.auth.changePassword,
  signup: API.auth.signup,
  fetchEmployerConfigurations: API.employer.fetchEmployerConfigurations,
};

export type AppProviderAPIValue = typeof localAPI;

const CreateProviderValue = ({ api = localAPI }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(AppReducer, AppState);
  const [shouldUseNewTransferTile, setShouldUseNewTransfertile] = useState<boolean>(true);
  const [isTestingTransferStatus, setIsTestingTransferStatus] = useState<boolean>(false);
  const { userId } = Session;

  const getUserDetails = async () => {
    try {
      dispatch({
        type: Actions.FETCHING_USER_DETAILS,
      });
      const userDetails = await api.getUserDetails(userId);
      Session.userIdentityId = userDetails.userDetailsId.toString();
      dispatch({
        payload: userDetails,
        type: Actions.GET_USER_DETAILS,
      });
      return userDetails;
    } catch (e) {
      Sentry.captureException(new Error(`Unable to fetch user details: ${e instanceof Error ? e.message : String(e)}`));
      Sentry.captureMessage("Unable to fetch user details");
      return navigate(route(RoutePaths.error));
    }
  };

  async function updateUserDetails(details: UserDetails) {
    try {
      await api.updateUserDetails({
        userId: Session.userId,
        details,
      });
      dispatch({
        payload: details,
        type: Actions.GET_USER_DETAILS,
      });
    } catch (e) {
      Sentry.captureException(
        new Error(`Unable to update user details: ${e instanceof Error ? e.message : String(e)}`)
      );
      Sentry.captureMessage("Unable to update user details");
      return navigate(route(RoutePaths.error));
    }
  }

  function updateGlobalCSSVar(property: keyof typeof GlobalStyleDefaults, value: any) {
    dispatch({
      type: Actions.UPDATE_GLOBAL_CSS_VAR,
      payload: {
        property,
        value,
      },
    });
  }

  function revertGlobalCSSVar(property: keyof typeof GlobalStyleDefaults) {
    dispatch({
      type: Actions.UPDATE_GLOBAL_CSS_VAR,
      payload: {
        property,
        value: GlobalStyleDefaults[property],
      },
    });
  }

  const openHamburgerMenu = () => {
    dispatch({
      type: Actions.SET_SHOW_HAMBURGER_MENU,
      payload: state.showHamburgerMenu,
    });
  };

  const setShowTopBarIcons = (showArrow = true, showHamburger = true) => {
    dispatch({
      type: Actions.SET_SHOW_TOP_BAR_ICONS,
      payload: { showArrow, showHamburger },
    });
  };

  const setShowTopBarGradient = (showGradient: boolean) => {
    dispatch({
      type: Actions.SET_SHOW_TOP_BAR_GRADIENT,
      payload: showGradient,
    });
  };

  const login = async ({ password, username }) => {
    const response = await api.login({ password, username });
    dispatch({
      type: Actions.CLEAR_USER_DETAILS_DATA,
    });
    const { accessToken } = response.authenticationResponse;
    Authentication.authenticate({
      accessToken,
      userId: response.userId,
      email: username,
    });
    Analytics.track({ action: HeapEventAction.completed, screen: "Login" });
  };

  const forgotPassword = async ({ username }) => {
    const response = await api.forgotPassword(username);
    const verificationCodeDeliveryBy = response.attributeName;
    dispatch({
      type: Actions.GET_VERIFICATION_CODE_DELIVERY_METHOD,
      payload: verificationCodeDeliveryBy,
    });
  };

  const resetPassword = async ({ confirmationCode, password, username }) => {
    try {
      await api.resetPassword({ confirmationCode, password, username });
    } catch (e) {
      Sentry.captureException(
        new Error(`Reset Forget Password call failed: ${e instanceof Error ? e.message : String(e)}`)
      );
      Sentry.captureMessage(`Reset Forgot Password Call Failed, ${e}`);
      throw new Error("Reset Forgot Password Call Failed");
    }
  };

  const getUser = async () => {
    dispatch({
      type: Actions.FETCHING_USER,
    });
    try {
      const user = await api.getUser(Session.userId);
      Authentication.updateHeap(user);
      Authentication.updateBeacon(user);
      dispatch({
        type: Actions.GET_USER,
        payload: user,
      });
      return user;
    } catch (e) {
      Sentry.captureException(new Error(`Failed to fetch user data: ${e instanceof Error ? e.message : String(e)}`));
      Sentry.captureMessage(`Failed to fetch user data, ${e}`);
      throw new Error("Failed to fetch user data");
    }
  };

  const changeEmail = async (newEmail) => {
    try {
      await api.updateEmail({
        userId: Session.userId,
        accessToken: Session.accessToken,
        newEmail,
      });
      dispatch({
        type: Actions.CHANGE_EMAIL,
      });
    } catch (e) {
      Sentry.captureException(new Error(`Changing Email Failed: ${e instanceof Error ? e.message : String(e)}`));
      Sentry.captureMessage(`Changing Email Failed, ${e}`);
      throw new Error("Changing Email Failed");
    }
  };

  const changePhone = async (newPhoneNumber) => {
    try {
      await api.updatePhoneNumber({
        userId: Session.userId,
        accessToken: Session.accessToken,
        newPhoneNumber,
      });
    } catch (e) {
      Sentry.captureException(new Error(`Changing Phone Number Failed: ${e instanceof Error ? e.message : String(e)}`));
      Sentry.captureMessage(`Changing Phone Number Failed, ${e}`);
      throw new Error("Changing Phone Number Failed");
    }
  };

  async function verifyEmail(email: string) {
    try {
      await api.verifyEmail(email);
    } catch (e) {
      Sentry.captureException(new Error(`Email Verification failed: ${e instanceof Error ? e.message : String(e)}`));
      Sentry.captureMessage(`Email Verification failed, ${e}`);
      throw new Error("Email Verification Failed");
    }
  }

  const changePassword = async (params: { previousPassword: string; proposedPassword: string }) => {
    try {
      await api.changePassword({
        accessToken: Session.accessToken,
        previousPassword: params.previousPassword,
        proposedPassword: params.proposedPassword,
      });
      dispatch({
        type: Actions.PASSWORD_CHANGED,
      });
    } catch (e) {
      Sentry.captureMessage(`Reset Forgot Password Call Failed, ${e}`);
      throw new Error("Unable to change password");
    }
  };

  const createDemoAccount = async (params: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    phoneNumber: string;
    vendorName?: VendorConfigIds;
    currentEmployee: boolean;
  }) => {
    const vendor = getVendor(params.vendorName as any);
    try {
      await api.signup({
        ...params,
        shouldSendNotification: false,
        employerName: vendor?.type === "employer" ? vendor.id : undefined,
        partnerName: vendor?.type === "partner" ? vendor.id : undefined,
      });
      setTimeout(() => verifyEmail(params.email), 1000);
    } catch (e: any) {
      Sentry.captureException(
        new Error(`Unable to create a new account: ${e instanceof Error ? e.message : String(e)}`)
      );
      throw new Error(`Unable to create a new account: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const createAccount = async (params: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    phoneNumber: string;
    vendorName?: string;
    currentEmployee: boolean;
    companyType?: SlugValidation["companyType"];
  }) => {
    try {
      await api.signup({
        ...params,
        shouldSendNotification: !isE2EEnvironment(),
        employerName: params.companyType === "EMP" ? params.vendorName : undefined,
        partnerName: params.companyType === "PTR" ? params.vendorName : undefined,
      });
    } catch (e: any) {
      if (e.message.includes("403")) {
        throw new Error("Unable to create a new account, as user email already exist (Status code: 403)");
      }
      throw new Error(`Unable to create account ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const getCountriesList = async () => {
    try {
      const response = await api.getCountriesList();
      const data = response.staticDataList || [];
      dispatch({
        type: Actions.GET_COUNTRIES,
        payload: data,
      });
    } catch (e) {
      throw new Error("Unable to fetch list of countries");
    }
  };

  const getStatesList = async () => {
    try {
      const response = await api.getStatesList();
      const data = response.staticDataList || [];
      dispatch({
        type: Actions.GET_STATES,
        payload: data,
      });
    } catch (e) {
      throw new Error("Failed to get list of states");
    }
  };

  const getSuffixList = async () => {
    try {
      const response = await api.getSuffixList();
      const data = response.staticDataList || [];

      dispatch({
        type: Actions.GET_SUFFIX,
        payload: data,
      });
    } catch (e) {
      throw new Error("Unable to fetch Suffix");
    }
  };

  const setVendorName = (path) => {
    dispatch({
      type: Actions.SET_VENDOR_NAME,
      payload: path,
    });
  };

  const resetEmployerConfig = () => {
    dispatch({
      type: Actions.REMOVE_EMPLOYER_CONFIGURATION,
    });
  };

  const getCurrentEmploymentStatus = async (employer: string) => {
    try {
      const response = await api.fetchEmployerConfigurations(employer, isDevelopmentEnvironment() ? "dev" : "prod");
      dispatch({
        type: Actions.GET_EMPLOYER_CONFIGURATION,
        payload: response as SlugValidation,
      });
    } catch (e) {
      Sentry.captureException(
        new Error(`Unable to fetch employer configuration: ${e instanceof Error ? e.message : String(e)}`)
      );
      Sentry.captureMessage(`Unable to fetch employer configuration ${e as unknown as string}`);
      throw new Error("Unable to fetch employer configuration");
    }
  };

  const getEmployerDetails = async () => {
    try {
      dispatch({
        type: Actions.FETCHING_EMPLOYER_DETAILS,
      });
      const response = await api.getEmployerDetails(Session.userId);
      dispatch({
        type: Actions.GET_EMPLOYER_DETAILS,
        payload: response,
      });
    } catch (e) {
      dispatch({
        type: Actions.GET_EMPLOYER_DETAILS,
        payload: new UserEmployerDetails(),
      });
      Sentry.captureException(
        new Error(`Unable to fetch employer details: ${e instanceof Error ? e.message : String(e)}`)
      );
      Sentry.captureMessage(`Unable to fetch employer details ${e as unknown as string}`);
      throw new Error("Unable to fetch employer details");
    }
  };

  const clearUserData = () => {
    dispatch({
      type: Actions.CLEAR_USER_DETAILS_DATA,
    });
  };

  const setNavigationBlockedReason = (reason: string) => {
    dispatch({
      type: Actions.SET_NAVIGATION_BLOCKED,
      payload: reason,
    });
  };

  const isNavigationBlocked = () => state.navigationBlockedReason !== "";

  return {
    ...state,
    clearUserData,
    openHamburgerMenu,
    getCountriesList,
    getStatesList,
    getSuffixList,
    forgotPassword,
    changeEmail,
    resetPassword,
    login,
    changePhone,
    verifyEmail,
    changePassword,
    getUser,
    getUserDetails,
    createAccount,
    setVendorName,
    setShowTopBarIcons,
    setShowTopBarGradient,
    getCurrentEmploymentStatus,
    getEmployerDetails,
    revertGlobalCSSVar,
    updateGlobalCSSVar,
    updateUserDetails,
    isNavigationBlocked,
    setNavigationBlockedReason,
    createDemoAccount,
    resetEmployerConfig,
    manifestIRAPartners, // IRA Integration
    manifestIRAPartnersLowerCase: manifestIRAPartners.map((e) => e.toLowerCase()),
    enableBettermentIntegration: manifestIRAPartners.includes("Betterment"),
    shouldUseNewTransferTile,
    setShouldUseNewTransfertile,
    isTestingTransferStatus,
    setIsTestingTransferStatus,
    userId: Session.userId,
  };
};

export const AppContext = createContext({} as AppProviderValue);

const AppProvider = (props: PropsWithChildren<any>) => {
  const providerValue = CreateProviderValue(props);
  return (
    <ThemeProvider theme={providerValue.theme}>
      <AppContext.Provider value={providerValue}>{props.children}</AppContext.Provider>
    </ThemeProvider>
  );
};

export default AppProvider;
